import {
  generatePatternStyles,
  hasGradient,
  type Pattern,
} from '@web-stories-wp/patterns';
import {
  decodeGradientPattern,
  getGradientPatternStyleValue,
} from '../../../utils/textGradient';
import compactListMarkup from '../../../utils/compactListMarkup';
import { sanitizeRichText } from '../../../utils/sanitizeMarkup';
import { generateFontFamily } from '../utils';
import type { TextElementToDisplay } from '../utils/types';
import type { TextElementFont, TextFontReferenceV1 } from '../../types';
import {
  getTextFontReferenceMap,
  getTextFontRenderFamily,
  parseInlineFontSize,
  parseInlineLineHeight,
  resolveTextFontWeight,
} from '../utils/mixedTypography';
import {
  getListMarkerGlyph,
  getListMarkerShape,
  getNormalizedTextListStyle,
  getOrderedListMarkerColumnWidth,
  hasSemanticList,
} from '../utils/listStyle';
import getListMarkerColor from '../utils/getListMarkerColor';
import getListMarkerBaselineOffset from '../utils/getListMarkerBaselineOffset';
import { parseTextContentBody } from '../utils/parseTextContentBody';

type GetSpanForElementProps = {
  dataToEditorX?: (value: number) => number;
  dataToEditorY: (value: number) => number;
  text: TextElementToDisplay;
  width: number;
};

type SpanProps = {
  color: string;
  content: string;
  fontWeight: number;
  fontStyle: string;
  font: TextElementFont;
  fontSize: number;
  fontSizeInDataUnits: number;
  fontSizeOverride?: number;
  lineHeight: number;
  lineHeightOverride?: number;
  renderFamily: string;
  fontReferenceId?: string;
  fontRevision?: string;
  gradient?: Pattern;
  gradientKey?: string;
  hasInlineColor: boolean;
  isNewLine: boolean;
  isListMarker?: boolean;
  letterSpacing: string;
  lineInset: number;
  lineNumber: number;
  lineOffsetY: number;
  markerOffsetY?: number;
  markerShape?: 'disc' | 'circle' | 'square';
  markerShapeHeight?: number;
  markerShapeOffsetY?: number;
  markerShapeStrokeWidth?: number;
  markerShapeWidth?: number;
  markerTextAlign?: 'start' | 'end';
  markerX?: number;
  textDecoration?: string;
  textTransform?: string;
  width: number;
  x: number;
};

type RawSpanProps = Omit<SpanProps, 'width' | 'x'>;

type SharedSpanProps = Pick<
  RawSpanProps,
  | 'color'
  | 'fontWeight'
  | 'fontStyle'
  | 'font'
  | 'fontSize'
  | 'fontSizeInDataUnits'
  | 'fontSizeOverride'
  | 'lineHeight'
  | 'lineHeightOverride'
  | 'renderFamily'
  | 'fontReferenceId'
  | 'fontRevision'
  | 'hasInlineColor'
  | 'letterSpacing'
  | 'gradient'
  | 'gradientKey'
  | 'textDecoration'
  | 'textTransform'
>;

type LineHeightSpanProps = Pick<
  SharedSpanProps,
  'lineHeight' | 'lineHeightOverride'
>;

function getLineHeightSpanProps({
  lineHeight,
  lineHeightOverride,
}: SharedSpanProps): LineHeightSpanProps {
  return {
    lineHeight,
    ...(lineHeightOverride !== undefined ? { lineHeightOverride } : {}),
  };
}

function getSharedSpanProps(
  text: TextElementToDisplay,
  element: HTMLElement,
  useGradientFill: boolean,
  dataToEditorY: (value: number) => number,
  referenceMap: Map<string, TextFontReferenceV1>,
  inherited?: SharedSpanProps
): SharedSpanProps {
  const defaultColor =
    text.color && !useGradientFill
      ? generatePatternStyles(text.color, 'color', text.color.id).color
      : '#000000';
  const defaults: SharedSpanProps = {
    color: defaultColor || '#000000',
    font: text.font,
    fontSize: dataToEditorY(text.fontSize),
    fontSizeInDataUnits: text.fontSize,
    lineHeight: text.lineHeight,
    renderFamily: text.font.family,
    fontWeight: text.fontWeight || 400,
    fontStyle: text.fontStyle || 'normal',
    hasInlineColor: false,
    letterSpacing: '0em',
  };
  const sharedSpanProps: SharedSpanProps = { ...(inherited || defaults) };

  if (element.tagName === 'SPAN') {
    if (element.hasAttribute('data-st-font-ref')) {
      const referenceId = element.getAttribute('data-st-font-ref') || '';
      const reference = referenceId ? referenceMap.get(referenceId) : undefined;
      if (reference) {
        sharedSpanProps.font = reference.font;
        sharedSpanProps.renderFamily = getTextFontRenderFamily(reference);
        sharedSpanProps.fontReferenceId = reference.id;
        sharedSpanProps.fontRevision = reference.revision;
      } else {
        sharedSpanProps.font = text.font;
        sharedSpanProps.renderFamily = text.font.family;
        sharedSpanProps.fontReferenceId = undefined;
        sharedSpanProps.fontRevision = undefined;
      }
    }

    if (element.hasAttribute('data-st-font-size')) {
      const fontSize = parseInlineFontSize(
        element.getAttribute('data-st-font-size')
      );
      const effectiveFontSize = fontSize ?? text.fontSize;
      sharedSpanProps.fontSize = dataToEditorY(effectiveFontSize);
      sharedSpanProps.fontSizeInDataUnits = effectiveFontSize;
      sharedSpanProps.fontSizeOverride = fontSize ?? undefined;
    }

    if (element.hasAttribute('data-st-line-height')) {
      const lineHeight = parseInlineLineHeight(
        element.getAttribute('data-st-line-height')
      );
      sharedSpanProps.lineHeight = lineHeight ?? text.lineHeight;
      sharedSpanProps.lineHeightOverride = lineHeight ?? undefined;
    }

    const gradientValue = getGradientPatternStyleValue(element.style);
    if (gradientValue) {
      const gradient = decodeGradientPattern(gradientValue);
      if (gradient) {
        sharedSpanProps.gradient = gradient;
        sharedSpanProps.gradientKey = gradientValue.trim();
        sharedSpanProps.hasInlineColor = true;
      }
    }

    if (element.style.fontWeight) {
      sharedSpanProps.fontWeight = resolveTextFontWeight(
        element.style.fontWeight,
        sharedSpanProps.fontWeight
      );
    }

    if (element.style.fontStyle) {
      sharedSpanProps.fontStyle = element.style.fontStyle;
    }

    if (element.style.letterSpacing) {
      sharedSpanProps.letterSpacing = element.style.letterSpacing;
    }

    const textDecoration =
      element.style.textDecoration || element.style.textDecorationLine;
    if (textDecoration) {
      sharedSpanProps.textDecoration = textDecoration;
    }

    if (element.style.textTransform) {
      sharedSpanProps.textTransform = element.style.textTransform;
    }

    if (!gradientValue && element.style.color) {
      sharedSpanProps.gradient = undefined;
      sharedSpanProps.gradientKey = undefined;
      sharedSpanProps.color = element.style.color;
      sharedSpanProps.hasInlineColor = true;
    }
  }

  if (['B', 'STRONG'].includes(element.tagName) && !element.style.fontWeight) {
    sharedSpanProps.fontWeight = 700;
  }
  if (['EM', 'I'].includes(element.tagName) && !element.style.fontStyle) {
    sharedSpanProps.fontStyle = 'italic';
  }
  if (element.tagName === 'U' && !sharedSpanProps.textDecoration) {
    sharedSpanProps.textDecoration = 'underline';
  }

  return sharedSpanProps;
}

export function getSpansForElement({
  dataToEditorX = (value) => value,
  dataToEditorY,
  text,
  width,
}: GetSpanForElementProps) {
  const spans: RawSpanProps[] = [];
  const useGradientFill = hasGradient(text.color);
  const referenceMap = getTextFontReferenceMap(text);
  const sanitizedContent = sanitizeRichText(text.content);
  const hasList = hasSemanticList(sanitizedContent);
  const normalizedContent = hasList
    ? compactListMarkup(sanitizedContent)
    : sanitizedContent;
  const normalizedListStyle = hasList
    ? getNormalizedTextListStyle(text.listStyle, text.fontSize)
    : undefined;
  const markerSize = normalizedListStyle?.marker.size || text.fontSize;
  const unorderedMarker = normalizedListStyle
    ? getListMarkerGlyph(normalizedListStyle, 'unordered')
    : '';
  const unorderedMarkerShape = normalizedListStyle
    ? getListMarkerShape(normalizedListStyle, text.fontSize, text.lineHeight)
    : null;
  const orderedMarkerFallbackColumn = dataToEditorX(markerSize);
  const unorderedMarkerColumn = hasList
    ? unorderedMarkerShape
      ? dataToEditorX(unorderedMarkerShape.size)
      : measureListMarkerWidth({
          fallbackWidth: orderedMarkerFallbackColumn,
          fontSize: dataToEditorY(markerSize),
          glyph: unorderedMarker,
          text,
        })
    : orderedMarkerFallbackColumn;
  const markerColor = hasList
    ? generatePatternStyles(getListMarkerColor(text), 'color').color ||
      '#000000'
    : '#000000';

  const parentElement = parseTextContentBody(normalizedContent);

  parentElement.style.width = `${width}px`;
  const orderedMarkerColumns = new WeakMap<HTMLElement, number>();
  for (const list of Array.from(parentElement.querySelectorAll('ol'))) {
    const itemCount = Array.from(list.children).filter(
      (child) => child.tagName === 'LI'
    ).length;
    orderedMarkerColumns.set(
      list,
      getOrderedListMarkerColumnWidth(
        itemCount,
        (glyph) =>
          measureListMarkerWidth({
            fallbackWidth: getMarkerFallbackWidth(
              glyph,
              orderedMarkerFallbackColumn
            ),
            fontSize: dataToEditorY(markerSize),
            glyph,
            text,
          }),
        orderedMarkerFallbackColumn
      )
    );
  }

  let currentLineIndex = 0;
  let currentLineSpans: RawSpanProps[] = [];
  let lastTokenWasLineBreak = false;
  let preserveTrailingEmptyLine = false;
  let currentLineInset = 0;
  let currentLineOffsetY = 0;
  let currentAvailableWidth = width;
  let lastEffectiveLineHeightProps: LineHeightSpanProps | null = null;

  let textFitterElement = document.createElement('p');
  applyTextFitterStyles(textFitterElement, text, dataToEditorY);

  document.body.appendChild(textFitterElement);

  const pushEmptyLineSpan = (sharedSpanProps: SharedSpanProps) => {
    spans.push({
      ...sharedSpanProps,
      ...(lastEffectiveLineHeightProps || {}),
      isNewLine: true,
      content: '',
      lineInset: currentLineInset,
      lineNumber: currentLineIndex,
      lineOffsetY: currentLineOffsetY,
    });
  };

  const finishCurrentLine = (
    sharedSpanProps: SharedSpanProps,
    isExplicitLineBreak = false
  ) => {
    if (currentLineSpans.length > 0) {
      const finalTextSpan = [...currentLineSpans]
        .reverse()
        .find((span) => !span.isListMarker);
      if (finalTextSpan) {
        lastEffectiveLineHeightProps = getLineHeightSpanProps(finalTextSpan);
      }
      spans.push(...currentLineSpans);
      currentLineSpans = [];
    } else if (lastTokenWasLineBreak || spans.length === 0) {
      pushEmptyLineSpan(sharedSpanProps);
    }

    currentLineIndex++;
    textFitterElement.textContent = '';
    lastTokenWasLineBreak = true;
    preserveTrailingEmptyLine = isExplicitLineBreak;
  };

  // A style/paint boundary does not end a word. Accumulate its styled parts
  // until whitespace or an explicit break, then choose one wrap for the word.
  type WordPart = { content: string; props: SharedSpanProps };
  let pendingWord: WordPart[] = [];
  let lastSpanProps: SharedSpanProps | null = null;

  const appendSpan = ({ content, props }: WordPart) => {
    const lastSpan = currentLineSpans[currentLineSpans.length - 1];
    if (lastSpan && !lastSpan.isListMarker && lastSpanProps === props) {
      lastSpan.content += content;
    } else {
      currentLineSpans.push({
        ...props,
        content,
        isNewLine: currentLineSpans.length === 0,
        lineInset: currentLineInset,
        lineNumber: currentLineIndex,
        lineOffsetY: currentLineOffsetY,
      });
    }
    lastSpanProps = props;
    lastEffectiveLineHeightProps = getLineHeightSpanProps(props);
    lastTokenWasLineBreak = false;
  };

  const createWordFitterElement = ({ content, props }: WordPart) => {
    const span = document.createElement('span');
    span.style.fontFamily = generateFontFamily(props.font, props.renderFamily);
    span.style.fontSize = `${props.fontSize}px`;
    span.style.fontWeight = props.fontWeight.toString();
    span.style.fontStyle = props.fontStyle;
    span.style.letterSpacing = props.letterSpacing;
    span.style.textTransform = props.textTransform || '';
    span.textContent = content;
    return span;
  };

  const getUpdatedTextFitterElement = (parts: WordPart[]) => {
    const updated = textFitterElement.cloneNode(true) as HTMLParagraphElement;
    for (const part of parts) {
      updated.appendChild(createWordFitterElement(part));
    }
    document.body.appendChild(updated);
    return updated;
  };

  const replaceTextFitterElement = (updated: HTMLParagraphElement) => {
    textFitterElement.remove();
    textFitterElement = updated;
  };

  const flushPendingWord = () => {
    if (!pendingWord.length) return;
    const parts = pendingWord;
    pendingWord = [];
    let candidate = getUpdatedTextFitterElement(parts);
    if (getMeasuredElementWidth(candidate) > currentAvailableWidth) {
      candidate.remove();
      if (currentLineSpans.some((span) => !span.isListMarker)) {
        finishCurrentLine(parts[0].props);
      }
      candidate = getUpdatedTextFitterElement(parts);
    }
    if (getMeasuredElementWidth(candidate) <= currentAvailableWidth) {
      replaceTextFitterElement(candidate);
      parts.forEach(appendSpan);
      return;
    }
    candidate.remove();

    // Oversized words keep the existing character fallback, across styles too.
    for (const { content, props } of parts) {
      for (const character of Array.from(content)) {
        const part = { content: character, props };
        let updated = getUpdatedTextFitterElement([part]);
        if (
          getMeasuredElementWidth(updated) > currentAvailableWidth &&
          currentLineSpans.some((span) => !span.isListMarker)
        ) {
          updated.remove();
          finishCurrentLine(props);
          updated = getUpdatedTextFitterElement([part]);
        }
        replaceTextFitterElement(updated);
        appendSpan(part);
      }
    }
  };

  const getAllSpansInElementFromElement = (
    element: HTMLElement,
    sharedSpanProps: SharedSpanProps
  ) => {
    if (element.tagName === 'BR') {
      flushPendingWord();
      finishCurrentLine(sharedSpanProps, true);
      return;
    }
    for (const word of getWordsAndSpaces(element.textContent || '')) {
      if (!word) continue;
      if (word === ' ' || word === '\n') {
        flushPendingWord();
        if (word === '\n') {
          finishCurrentLine(sharedSpanProps, true);
        } else {
          // Separator spaces stay on this line until the following word wraps.
          const part = { content: word, props: sharedSpanProps };
          replaceTextFitterElement(getUpdatedTextFitterElement([part]));
          appendSpan(part);
        }
      } else {
        pendingWord.push({ content: word, props: sharedSpanProps });
      }
    }
  };

  const getNestedSpans = (
    element: Element,
    sharedSpanPropsFromParent?: SharedSpanProps
  ) => {
    const sharedSpanProps = getSharedSpanProps(
      text,
      element as HTMLElement,
      useGradientFill,
      dataToEditorY,
      referenceMap,
      sharedSpanPropsFromParent
    );

    if (hasList && normalizedListStyle && element instanceof HTMLElement) {
      const tagName = element.tagName;

      if (tagName === 'UL' || tagName === 'OL') {
        for (const child of Array.from(element.children)) {
          if (child.tagName === 'LI') {
            getNestedSpans(child, sharedSpanProps);
          }
        }
        return;
      }

      if (tagName === 'P') {
        flushPendingWord();
        if (currentLineSpans.some((span) => !span.isListMarker)) {
          finishCurrentLine(sharedSpanProps);
        }

        for (const child of Array.from(element.childNodes)) {
          getNestedSpans(child as HTMLElement, sharedSpanProps);
        }

        flushPendingWord();
        if (currentLineSpans.length > 0) {
          finishCurrentLine(sharedSpanProps);
        }
        return;
      }

      if (tagName === 'LI') {
        flushPendingWord();
        if (currentLineSpans.length > 0) {
          finishCurrentLine(sharedSpanProps);
        }

        const parentList = element.parentElement;
        const isOrderedList = parentList?.tagName === 'OL';
        const ancestorLists = getAncestorLists(parentList);
        const listDepth = Math.max(0, ancestorLists.length - 1);
        const itemIndex = getListItemIndex(element);
        const markerColumn = isOrderedList
          ? (parentList ? orderedMarkerColumns.get(parentList) : undefined) ??
            orderedMarkerFallbackColumn
          : unorderedMarkerColumn;
        const markerGap = dataToEditorX(normalizedListStyle.markerGap);
        const listPadding =
          dataToEditorX(normalizedListStyle.indent) +
          listDepth * dataToEditorX(normalizedListStyle.nestedIndent);
        const ancestorMarkerColumns = ancestorLists.reduce(
          (total, list) =>
            total +
            (list.tagName === 'OL'
              ? orderedMarkerColumns.get(list) ?? orderedMarkerFallbackColumn
              : unorderedMarkerColumn) +
            markerGap,
          0
        );
        const previousInset = currentLineInset;
        const previousAvailableWidth = currentAvailableWidth;

        currentLineInset = Math.min(width, listPadding + ancestorMarkerColumns);
        currentAvailableWidth = Math.max(0, width - currentLineInset);

        if (itemIndex > 0 || listDepth > 0) {
          currentLineOffsetY += dataToEditorY(normalizedListStyle.itemSpacing);
        }

        const marker = getListMarkerGlyph(
          normalizedListStyle,
          isOrderedList ? 'ordered' : 'unordered',
          itemIndex + 1
        );
        const markerShape = isOrderedList ? null : unorderedMarkerShape;
        const markerHorizontalShift =
          !isOrderedList && normalizedListStyle.marker.preset === 'custom'
            ? dataToEditorX(normalizedListStyle.marker.horizontalShift || 0)
            : 0;
        currentLineSpans.push({
          ...sharedSpanProps,
          color: markerColor,
          content: markerShape ? '' : marker,
          font: text.font,
          fontReferenceId: undefined,
          fontRevision: undefined,
          fontSize: dataToEditorY(markerSize),
          fontSizeInDataUnits: markerSize,
          fontSizeOverride: undefined,
          lineHeight: text.lineHeight,
          lineHeightOverride: undefined,
          fontStyle: text.fontStyle || 'normal',
          fontWeight: text.fontWeight || 400,
          gradient: undefined,
          gradientKey: undefined,
          hasInlineColor: true,
          isListMarker: true,
          isNewLine: true,
          letterSpacing: 'normal',
          lineInset: currentLineInset,
          lineNumber: currentLineIndex,
          lineOffsetY: currentLineOffsetY,
          markerOffsetY: markerShape
            ? undefined
            : dataToEditorY(
                getListMarkerBaselineOffset({
                  fontMetrics: text.font?.metrics,
                  fontSize: text.fontSize,
                  lineHeight: text.lineHeight,
                  markerSize,
                  markerBaselineShift: normalizedListStyle.marker.baselineShift,
                })
              ),
          markerShape: markerShape?.preset,
          markerShapeHeight: markerShape
            ? dataToEditorY(markerShape.size)
            : undefined,
          markerShapeOffsetY: markerShape
            ? dataToEditorY(markerShape.offset)
            : undefined,
          markerShapeStrokeWidth: markerShape
            ? dataToEditorY(markerShape.strokeWidth)
            : undefined,
          markerShapeWidth: markerShape
            ? dataToEditorX(markerShape.size)
            : undefined,
          markerTextAlign: isOrderedList ? 'end' : 'start',
          markerX: isOrderedList
            ? currentLineInset - markerGap
            : Math.max(0, currentLineInset - markerColumn - markerGap) +
              markerHorizontalShift,
          renderFamily: text.font.family,
        });
        lastTokenWasLineBreak = false;

        const nestedLists: Element[] = [];
        for (const child of Array.from(element.childNodes)) {
          if (
            child instanceof HTMLElement &&
            (child.tagName === 'UL' || child.tagName === 'OL')
          ) {
            nestedLists.push(child);
          } else {
            getNestedSpans(child as HTMLElement, sharedSpanProps);
          }
        }

        flushPendingWord();
        if (currentLineSpans.length > 0) {
          finishCurrentLine(sharedSpanProps);
        }

        for (const nestedList of nestedLists) {
          getNestedSpans(nestedList, sharedSpanProps);
        }

        currentLineInset = previousInset;
        currentAvailableWidth = previousAvailableWidth;
        return;
      }
    }

    // An empty authoritative span is a persisted blank logical line, not
    // an additional break. Its surrounding breaks still own the line count.
    if (
      element.nodeType === 1 &&
      element.tagName === 'SPAN' &&
      element.childNodes.length === 0 &&
      parseInlineLineHeight(element.getAttribute('data-st-line-height')) !==
        null &&
      currentLineSpans.length === 0
    ) {
      currentLineSpans.push({
        ...sharedSpanProps,
        content: '',
        isNewLine: true,
        lineInset: currentLineInset,
        lineNumber: currentLineIndex,
        lineOffsetY: currentLineOffsetY,
      });
      return;
    }

    if (element.childNodes && Array.from(element.childNodes).length > 0) {
      for (const child of Array.from(element.childNodes)) {
        getNestedSpans(child as HTMLElement, sharedSpanProps);
      }
    } else {
      getAllSpansInElementFromElement(element as HTMLElement, sharedSpanProps);
    }
  };

  // In some cases (for example when editing), new lines are inside text nodes, not in <span> tags
  const children = Array.from(parentElement.childNodes);

  for (const element of children) {
    getNestedSpans(element as HTMLElement);
  }

  flushPendingWord();
  if (currentLineSpans.length > 0) {
    spans.push(...currentLineSpans);
  } else if (lastTokenWasLineBreak && (!hasList || preserveTrailingEmptyLine)) {
    pushEmptyLineSpan(
      getSharedSpanProps(
        text,
        parentElement as HTMLElement,
        useGradientFill,
        dataToEditorY,
        referenceMap
      )
    );
  }

  const measuredSpans = measureSpanPositions(trimLineEndSpaces(spans));

  document.body.removeChild(textFitterElement);

  return measuredSpans;
}

function getAncestorLists(list: HTMLElement | null) {
  const lists: HTMLElement[] = [];
  let current: HTMLElement | null = list;

  while (current) {
    if (current.tagName === 'UL' || current.tagName === 'OL') {
      lists.unshift(current);
    }
    current = current.parentElement;
  }

  return lists;
}

function getListItemIndex(item: HTMLElement) {
  const siblings = Array.from(item.parentElement?.children || []).filter(
    (element) => element.tagName === 'LI'
  );
  return Math.max(0, siblings.indexOf(item));
}

function applyTextFitterStyles(
  element: HTMLParagraphElement,
  text: TextElementToDisplay,
  dataToEditorY: (value: number) => number
) {
  element.style.fontFamily = generateFontFamily(text.font);
  element.style.fontSize = `${dataToEditorY(text.fontSize)}px`;
  element.style.width = 'max-content';
  element.style.whiteSpace = 'pre';
  element.style.margin = '0';
  element.style.padding = '0';
  element.style.opacity = '0';
  element.style.position = 'absolute';
  element.style.top = '0';
  element.style.left = '0';
  element.style.pointerEvents = 'none';
}

function getMeasuredElementWidth(element: HTMLElement) {
  return element.getBoundingClientRect().width || element.clientWidth;
}

function getMarkerFallbackWidth(glyph: string, markerSize: number) {
  const digitCount = Math.max(1, Array.from(glyph.replace(/\.$/, '')).length);
  return Math.max(markerSize, markerSize * (digitCount * 0.6 + 0.3));
}

function measureListMarkerWidth({
  fallbackWidth,
  fontSize,
  glyph,
  text,
}: {
  fallbackWidth: number;
  fontSize: number;
  glyph: string;
  text: TextElementToDisplay;
}) {
  const markerElement = document.createElement('span');
  markerElement.style.fontFamily = generateFontFamily(text.font);
  markerElement.style.fontSize = `${fontSize}px`;
  markerElement.style.fontStyle = text.fontStyle || 'normal';
  markerElement.style.fontWeight = `${text.fontWeight || 400}`;
  markerElement.style.letterSpacing = 'normal';
  markerElement.style.opacity = '0';
  markerElement.style.position = 'absolute';
  markerElement.style.whiteSpace = 'pre';
  markerElement.textContent = glyph;

  document.body.appendChild(markerElement);
  const measuredWidth = getMeasuredElementWidth(markerElement);
  document.body.removeChild(markerElement);

  return measuredWidth || fallbackWidth;
}

function trimLineEndSpaces(spans: RawSpanProps[]): RawSpanProps[] {
  const result = spans.map((span) => ({ ...span }));
  let currentLineNumber = result.length > 0 ? result[0].lineNumber : 0;
  let currentLineStartIndex = 0;

  const trimLine = (startIndex: number, endIndex: number) => {
    for (let index = endIndex - 1; index >= startIndex; index--) {
      const span = result[index];
      const nextContent = span.content.replace(/[ \t]+$/g, '');

      if (nextContent !== span.content) {
        result[index] = {
          ...span,
          content: nextContent,
        };
      }

      if (nextContent.length > 0) {
        return;
      }
    }
  };

  result.forEach((span, index) => {
    if (span.lineNumber === currentLineNumber) {
      return;
    }

    trimLine(currentLineStartIndex, index);
    currentLineNumber = span.lineNumber;
    currentLineStartIndex = index;
  });

  trimLine(currentLineStartIndex, result.length);

  return result.filter((span) => span.content || span.isNewLine);
}

function getWordsAndSpaces(text: string): string[] {
  const characters = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('');
  const wordsAndSpaces: string[] = [];

  let currentWord = '';

  for (const character of characters) {
    if (character === ' ' || character === '\n') {
      wordsAndSpaces.push(currentWord);
      wordsAndSpaces.push(character);
      currentWord = '';
      continue;
    }

    currentWord += character;
  }

  wordsAndSpaces.push(currentWord);

  return wordsAndSpaces;
}

function measureSpanPositions(spans: RawSpanProps[]): SpanProps[] {
  if (spans.length === 0) {
    return [];
  }

  const measureElement = document.createElement('span');
  measureElement.style.opacity = '0';
  measureElement.style.position = 'absolute';
  measureElement.style.top = '0';
  measureElement.style.left = '0';
  measureElement.style.whiteSpace = 'pre';

  document.body.appendChild(measureElement);

  let currentLineNumber = spans[0].lineNumber;
  let currentLineWidth = spans[0].lineInset;

  const measuredSpans = spans.map((span) => {
    if (span.lineNumber !== currentLineNumber) {
      currentLineNumber = span.lineNumber;
      currentLineWidth = span.lineInset;
    }

    measureElement.style.fontWeight = span.fontWeight.toString();
    measureElement.style.fontStyle = span.fontStyle;
    measureElement.style.fontFamily = generateFontFamily(
      span.font,
      span.renderFamily
    );
    measureElement.style.fontSize = `${span.fontSize}px`;
    measureElement.style.letterSpacing = span.letterSpacing;
    measureElement.style.textTransform = span.textTransform || '';
    measureElement.textContent = span.content;

    const width =
      span.markerShapeWidth ?? getMeasuredElementWidth(measureElement);
    const markerX = span.markerX ?? Math.max(0, span.lineInset - width);
    const x = span.isListMarker
      ? span.markerTextAlign === 'end'
        ? markerX - width
        : markerX
      : currentLineWidth;

    if (!span.isListMarker) {
      currentLineWidth += width;
    }

    return {
      ...span,
      width,
      x,
    };
  });

  document.body.removeChild(measureElement);

  return measuredSpans;
}
